import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid, Typography } from '@mui/material';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { FormModal } from 'components/molecules/Modal';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useRejectCaseFormSchema from 'pages/CaseManagementPage/CasesPage/CaseDetailsPage/RejectCaseForm/schemaValidation';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { useDictionary } from 'models/dictionaryContext';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { IRejectCaseFormValues } from 'models/cases';

const initialFormValues: IRejectCaseFormValues = { reasonUniqueIds: [], comment: '' };

interface IRejectCaseForm {
  caseId: string;
  close: () => void;
  isOpen: boolean;
  updateData: () => void;
}

const RejectCaseForm = ({ caseId, close, isOpen, updateData }: IRejectCaseForm) => {
  const { t } = useTranslations();
  const { handleRejectCase } = useCase();
  const { caseRejectionReason, getCaseRejectionReason } = useDictionary();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const { schema } = useRejectCaseFormSchema();
  const { clearAlert, modalAlert } = useAlert();

  useEffect(() => {
    if (!caseRejectionReason) {
      getCaseRejectionReason();
    }
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IRejectCaseFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IRejectCaseFormValues>,
  });

  const onSubmit = async (data: IRejectCaseFormValues) => {
    if (isSubmitLoading) return;
    setSubmitLoading(true);
    try {
      const res = await handleRejectCase(caseId, {
        reasonUniqueIds: data.reasonUniqueIds,
        comment: data.comment,
      });
      if (res) {
        reset(initialFormValues);
        close();
        clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
        await updateData();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormModal
        open={isOpen}
        onClickClose={() => {
          close();
          clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(onSubmit)}
        title={t('caseManagement.cases.rejectCase.title')}
        titleLeft
      >
        <Grid container sx={{ gap: 2, mb: 2.5 }}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2">
              {t('caseManagement.cases.rejectCaseInfo.text')}
            </Typography>
            <Typography variant="body2">
              {t('caseManagement.cases.rejectCaseInfo2.text')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormMultiSelect
              name="reasonUniqueIds"
              label={t('caseManagement.cases.decisionReason.label')}
              options={mapDictionaryToOptionProp(caseRejectionReason)}
              setValue={setValue}
              control={control}
              errors={errors}
              searchable={false}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="comment"
              type={EFormInputTypes.textarea}
              label={t('caseManagement.cases.decisionComment.label')}
              setValue={setValue}
              control={control}
              errors={errors}
              multiline
              minRows={5}
              charsLimit={1000}
              showCounter
              required
              id="reject_case_form_decision_comment-input"
            />
          </Grid>
        </Grid>
        {modalAlert && (
          <Grid size={{ xs: 12 }}>
            <Box>
              <Alert text={modalAlert.text} variant={modalAlert.variant} />
            </Box>
          </Grid>
        )}
      </FormModal>
    </form>
  );
};

export default RejectCaseForm;
